/*
  Ridge & River: "Load more" for collection pages (Warehouse v7).

  Works on top of the theme's own pagination instead of replacing it:
  - The server still renders normal ?page=N links, so search engines and
    no-JS visitors get plain pagination.
  - On page 1 this hides those links and shows "You've viewed 24 of 64" with
    a progress bar and a Load more button. The button is a real link to the
    next page, so it still works if this script fails.
  - Each click fetches the next page through the Section Rendering API (the
    same request Warehouse makes for filters) and appends the products.
  - The URL is left alone. How many pages were loaded and the scroll position
    are kept in sessionStorage, so the back button returns shoppers to the
    product they left from.
  - Warehouse re-renders the grid when filters or sorting change; a
    MutationObserver re-attaches the button to the new grid.
*/
(function () {
  'use strict';

  // Loaded once per page even if more than one section includes it.
  if (window.rrLoadMoreReady) return;
  window.rrLoadMoreReady = true;

  var STORE_PREFIX = 'rr-load-more:';
  var section = document.querySelector('[data-section-type="collection"]');
  if (!section || !window.fetch) return;

  var busy = false;

  function stateKey() {
    var url = new URL(window.location.href);
    url.searchParams.delete('page');
    return STORE_PREFIX + url.pathname + url.search;
  }

  function readState() {
    try {
      return JSON.parse(sessionStorage.getItem(stateKey())) || null;
    } catch (e) {
      return null;
    }
  }

  function writeState(patch) {
    try {
      var state = readState() || {};
      for (var k in patch) state[k] = patch[k];
      sessionStorage.setItem(stateKey(), JSON.stringify(state));
    } catch (e) {}
  }

  function dynamicPart() {
    return section.querySelector('.collection__dynamic-part');
  }

  function productList(root) {
    return root.querySelector('.product-list--collection');
  }

  function readCounts(root) {
    var node = root.querySelector('[data-collection-products-count]');
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  function totalFrom(root) {
    var counts = readCounts(root);
    if (!counts) return 0;
    return parseInt(String(counts.productsCount).replace(/[^0-9]/g, ''), 10) || 0;
  }

  function nextHref(root) {
    var link = root.querySelector('.pagination .pagination__next');
    return link ? link.href : '';
  }

  function render(box, loaded, total, href) {
    var pct = total ? Math.min(100, Math.round((loaded / total) * 100)) : 100;
    box.querySelector('[data-rr-lm-loaded]').textContent = loaded;
    box.querySelector('[data-rr-lm-total]').textContent = total;
    box.querySelector('.rr-lm__fill').style.width = pct + '%';
    var btn = box.querySelector('.rr-lm__btn');
    if (href) {
      btn.href = href;
      btn.hidden = false;
    } else {
      btn.hidden = true;
    }
  }

  function enhance() {
    var dyn = dynamicPart();
    if (!dyn || dyn.querySelector('.rr-lm')) return;
    var list = productList(dyn);
    if (!list) return;

    var pagination = dyn.querySelector('.pagination');
    // Landed directly on page 2+ (old link, search result): keep the theme's
    // numbered pagination so earlier products stay reachable.
    if (pagination && pagination.querySelector('.pagination__prev')) return;

    var href = nextHref(dyn);
    if (!href) return;

    var box = document.createElement('div');
    box.className = 'rr-lm';
    box.innerHTML =
      '<p class="rr-lm__count" aria-live="polite">You’ve viewed <strong data-rr-lm-loaded></strong> of <strong data-rr-lm-total></strong> products</p>' +
      '<div class="rr-lm__bar" aria-hidden="true"><span class="rr-lm__fill"></span></div>' +
      '<a class="rr-lm__btn button button--primary" href="#">Load more</a>';

    if (pagination) {
      pagination.hidden = true;
      pagination.insertAdjacentElement('afterend', box);
    } else {
      list.insertAdjacentElement('afterend', box);
    }

    render(box, list.querySelectorAll('.product-item').length, totalFrom(dyn), href);
  }

  function updateShowingCount(fetchedRoot, loaded) {
    var counts = readCounts(fetchedRoot);
    if (!counts || !counts.showingCount) return;
    // "Showing 25 - 48 of 64 products" -> "Showing 1 - 48 of 64 products"
    var text = String(counts.showingCount).replace(/[0-9]+/, '1');
    section.querySelectorAll('.collection__showing-count, .collection__products-count-showing').forEach(function (el) {
      el.textContent = text;
    });
  }

  function loadNext() {
    var dyn = dynamicPart();
    var box = dyn && dyn.querySelector('.rr-lm');
    var btn = box && box.querySelector('.rr-lm__btn');
    if (!btn || btn.hidden || busy) return Promise.resolve(false);

    busy = true;
    btn.setAttribute('aria-busy', 'true');
    btn.classList.add('rr-lm__btn--loading');
    btn.textContent = 'Loading…';
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    var url = new URL(btn.href, window.location.origin);
    url.searchParams.set('section_id', section.getAttribute('data-section-id'));

    return fetch(url.toString(), { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var temp = document.createElement('div');
        temp.innerHTML = html;
        var incoming = productList(temp);
        var list = productList(dynamicPart());
        if (!incoming || !list) throw new Error('No product list in response');

        var firstNew = null;
        Array.prototype.slice.call(incoming.children).forEach(function (item) {
          list.appendChild(item);
          if (!firstNew) firstNew = item;
        });

        var loaded = list.querySelectorAll('.product-item').length;
        // Keep the first page's total: search result counts can drift between pages.
        render(box, loaded, totalFrom(dynamicPart()) || totalFrom(temp), nextHref(temp));
        updateShowingCount(temp, loaded);

        var page = parseInt(url.searchParams.get('page'), 10) || 2;
        writeState({ pages: page });
        return true;
      })
      .catch(function () {
        // Fall back to a normal page load of the next page.
        window.location.href = btn.href;
        return false;
      })
      .then(function (ok) {
        busy = false;
        btn.removeAttribute('aria-busy');
        btn.classList.remove('rr-lm__btn--loading');
        btn.textContent = 'Load more';
        document.dispatchEvent(new CustomEvent('theme:loading:end'));
        return ok;
      });
  }

  // Delegated so it survives Warehouse swapping the grid.
  section.addEventListener('click', function (event) {
    var btn = event.target.closest('.rr-lm__btn');
    if (!btn) return;
    event.preventDefault();
    loadNext();
  });

  // Re-attach after filters / sorting re-render the grid.
  var dyn = dynamicPart();
  if (dyn && window.MutationObserver) {
    new MutationObserver(function () {
      enhance();
    }).observe(dyn, { childList: true });
  }

  // Remember the scroll position for the back button.
  var scrollTimer = null;
  window.addEventListener('scroll', function () {
    if (scrollTimer) return;
    scrollTimer = setTimeout(function () {
      scrollTimer = null;
      if ((readState() || {}).pages > 1) writeState({ y: window.scrollY });
    }, 250);
  }, { passive: true });

  function restore() {
    var nav = performance.getEntriesByType && performance.getEntriesByType('navigation')[0];
    var state = readState();
    if (!nav || nav.type !== 'back_forward' || !state || !(state.pages > 1)) {
      // A fresh visit starts at page 1 again.
      try { sessionStorage.removeItem(stateKey()); } catch (e) {}
      return;
    }
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    var target = state.pages;
    var y = state.y || 0;
    (function step(current) {
      if (current >= target) {
        window.scrollTo(0, y);
        return;
      }
      loadNext().then(function (ok) {
        if (ok) step(current + 1);
      });
    })(1);
  }

  enhance();
  restore();
})();